import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { isEqual } from 'lodash'

import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'

// MUI Imports
import {
  IconButton,
  InputAdornment,
  Grid,
  Dialog,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
  TextField,
  MenuItem,
  Radio,
  RadioGroup,
  FormControl,
  Card,
  CardContent
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import EmailIcon from '@mui/icons-material/Email'
import SettingsIcon from '@mui/icons-material/Settings'

// Component Imports
import DialogCloseButton from '../atoms/DialogCloseButton'
import BusinessCompanyInfoSection from '../molecules/BusinessCompanyInfoSection'
import EmailPurposeTargetingSection from '../molecules/EmailPurposeTargetingSection'
import { GmailConnectButton } from '../../../../../components/preview/Preview'

import { updateUser, testSendSettings, testReceiveSettings } from '../../../services/B2BServices'
import {
  getUserDetailsAndProcess
} from '../../../../../services/DataServiceMethods'

import { useToast } from '../../../../../campaigns/hooks/useToast'

import {
  useGlobalStoreActions,
  useGlobalStoreData
} from '../../../../../store/globalStore'

import Box from '@mui/material/Box'

import { countryEN } from '../../../../../assets/data/country-en'
import { countryTR } from '../../../../../assets/data/country-tr'
import { TRADE_TYPES, LANGUAGES } from '../../../utils/campaignConstants'

const McTransportStrategy = [ // mc-backend ENUM
  {
    label: 'SMTP',
    value: 'SMTP'
  },
  {
    label: 'SMTPS',
    value: 'SMTPS'
  },
  {
    label: 'SMTP_TLS',
    value: 'SMTP_TLS'
  },
  {
    label: 'SMTP_OAUTH2',
    value: 'SMTP_OAUTH2'
  }
]

const EmailReceiveProtocol = [ // mc-backend ENUM
  {
    label: 'IMAP', // imap
    value: 'IMAP'
  },
  {
    label: 'IMAPS', // imaps
    value: 'IMAPS'
  },
  {
    label: 'POP3', // pop3
    value: 'POP3'
  }
]

const EditUserEmailSettingsModal = (props) => {
  const {
    userEditState,
    close,
    userGSD,
    open
  } = props
  const { t } = useTranslation()

  const { showError, showSuccess } = useToast()

  const { setUserData } = useGlobalStoreActions()

  const languageGSD = useGlobalStoreData.language()

  const [loading, setLoading] = useState(false)
  const [isPasswordShown, setIsPasswordShown] = useState(false)

  const [testingSender, setTestingSender] = useState(false)
  const [testingReceiver, setTestingReceiver] = useState(false)

  const [activeTab, setActiveTab] = useState('1')
  const [connectionType, setConnectionType] = useState(
    userEditState?.emailSettings?.accountType === 'GMAIL_OAUTH2' ? 'gmail' : 'manual'
  )

  const initialEmailSettingsRef = useRef(null)

  const handleChange = useCallback((_event, newValue) => {
    setActiveTab(newValue)
  }, [])

  const companyValidationRules = useMemo(() => ({
    required: { required: 'This field is required' },
    email: {
      required: 'Email is required',
      pattern: {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: 'Invalid email address'
      }
    },
    keyProducts: {
      validate: (value) => {
        if (!value || value.length === 0) {
          return 'At least one key product is required'
        }
        return true
      }
    }
  }), [])

  const transportStrategyOptions = useMemo(() => McTransportStrategy, [])
  const emailReceiveProtocolOptions = useMemo(() => EmailReceiveProtocol, [])

  const getLocalizedCountryName = useCallback((countryName) => {
    if (!countryName) return ''

    const currentCountries = languageGSD === 'en' ? countryEN : countryTR
    const otherCountries = languageGSD === 'en' ? countryTR : countryEN

    const foundInCurrent = currentCountries.find(c => c.countryName === countryName)
    if (foundInCurrent) {
      return countryName
    }

    const foundInOther = otherCountries.find(c => c.countryName === countryName)
    if (foundInOther) {
      const translatedCountry = currentCountries.find(c => c.countryCode === foundInOther.countryCode)
      if (translatedCountry) {
        return translatedCountry.countryName
      }
    }

    return countryName
  }, [languageGSD])

  const {
    control,
    reset,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm()

  const watchedValues = watch()

  const isTestSenderDisabled = useMemo(() => {
    return testingSender ||
      !watchedValues?.emailUserName?.trim() ||
      !watchedValues?.emailPassword?.trim() ||
      !watchedValues?.smtpHostName?.trim() ||
      !watchedValues?.smtpPort?.toString()?.trim() ||
      !watchedValues?.transportStrategy?.trim()
  }, [
    testingSender,
    watchedValues?.emailUserName,
    watchedValues?.emailPassword,
    watchedValues?.smtpHostName,
    watchedValues?.smtpPort,
    watchedValues?.transportStrategy
  ])

  const isTestReceiverDisabled = useMemo(() => {
    return testingReceiver ||
      !watchedValues?.emailUserName?.trim() ||
      !watchedValues?.emailPassword?.trim() ||
      !watchedValues?.rxHostName?.trim() ||
      !watchedValues?.rxPort?.toString()?.trim() ||
      !watchedValues?.rxProtocol?.trim() ||
      !watchedValues?.inboxFolderName?.trim()
  }, [
    testingReceiver,
    watchedValues?.emailUserName,
    watchedValues?.emailPassword,
    watchedValues?.rxHostName,
    watchedValues?.rxPort,
    watchedValues?.rxProtocol,
    watchedValues?.inboxFolderName
  ])

  const defaultValues = useMemo(() => ({
    emailUserName: userEditState?.emailSettings?.emailUserName || '',
    emailPassword: userEditState?.emailSettings?.emailPassword || '',
    smtpHostName: userEditState?.emailSettings?.smtpHostName || '',
    smtpPort: userEditState?.emailSettings?.smtpPort || '',
    transportStrategy: userEditState?.emailSettings?.transportStrategy || '',
    rxHostName: userEditState?.emailSettings?.rxHostName || '',
    rxPort: userEditState?.emailSettings?.rxPort || '',
    rxProtocol: userEditState?.emailSettings?.rxProtocol || '',
    inboxFolderName: userEditState?.emailSettings?.inboxFolderName || '',

    'senderCompany.name': userEditState?.senderCompany?.name || '',
    'senderCompany.industry': userEditState?.senderCompany?.industry || '',
    'senderCompany.country': getLocalizedCountryName(userEditState?.senderCompany?.country || ''),
    'senderCompany.contact_person': userEditState?.senderCompany?.contact_person || '',
    'senderCompany.contact_title': userEditState?.senderCompany?.contact_title || '',
    'senderCompany.email': userEditState?.emailSettings?.emailUserName || '',
    'senderCompany.key_products': userEditState?.senderCompany?.key_products || [],
    'senderCompany.website': userEditState?.senderCompany?.website || '',
    'senderCompany.phone': userEditState?.senderCompany?.phone || '',
    'senderCompany.production_capacity': userEditState?.senderCompany?.production_capacity || '',
    'senderCompany.facilities': userEditState?.senderCompany?.facilities || [],
    'senderCompany.certifications': userEditState?.senderCompany?.certifications || [],
    'senderCompany.market_experience': userEditState?.senderCompany?.market_experience || [],
    'senderCompany.company_values': userEditState?.senderCompany?.company_values || [],

    'emailPurpose.target_market': getLocalizedCountryName(userEditState?.emailPurpose?.target_market || ''),
    'emailPurpose.trade_type': userEditState?.emailPurpose?.trade_type || TRADE_TYPES.EXPORT,
    'emailPurpose.product_category': userEditState?.emailPurpose?.product_category || '',
    'emailPurpose.language': userEditState?.emailPurpose?.language || LANGUAGES.ENGLISH,
    'emailPurpose.specific_products': userEditState?.emailPurpose?.specific_products || [],
    'emailPurpose.call_to_action': userEditState?.emailPurpose?.call_to_action || ''
  }), [userEditState, getLocalizedCountryName])

  const handleClose = useCallback(() => {
    reset()
    close()
  }, [reset, close])

  const handleClickShowPassword = useCallback(() => {
    setIsPasswordShown((show) => !show)
  }, [])

  const handleConnectionTypeChange = useCallback((event) => {
    setConnectionType(event.target.value)
  }, [])

  const testSenderSettings = useCallback((val) => {
    const accountType = connectionType === 'gmail' ? 'GMAIL_OAUTH2' : 'USERNAME_PASSWORD'

    const emailSettings = {
      accountType,
      emailUserName: val.emailUserName,
      emailPassword: val.emailPassword,
      smtpHostName: val.smtpHostName,
      smtpPort: Number(val.smtpPort),
      transportStrategy: val.transportStrategy,
      rxHostName: val.rxHostName,
      rxPort: Number(val.rxPort),
      rxProtocol: val.rxProtocol,
      inboxFolderName: val.inboxFolderName,
      accessToken: userEditState?.emailSettings?.accessToken || null,
      refreshToken: userEditState?.emailSettings?.refreshToken || null,
      expireTimeInSeconds: userEditState?.emailSettings?.expireTimeInSeconds || null
    }

    setTestingSender(true)
    testSendSettings(emailSettings).then(() => {
      showSuccess(t('mailTemplate.users.modal.senderTestSuccess'))
    }).catch(() => {
      showError(t('mailTemplate.users.modal.senderTestFailed'))
    }).finally(() => {
      setTestingSender(false)
    })
  }, [showSuccess, showError, t, connectionType, userEditState?.emailSettings?.accessToken, userEditState?.emailSettings?.refreshToken, userEditState?.emailSettings?.expireTimeInSeconds])

  const testReceiverSettings = useCallback((val) => {
    const accountType = connectionType === 'gmail' ? 'GMAIL_OAUTH2' : 'USERNAME_PASSWORD'

    const emailSettings = {
      accountType,
      emailUserName: val.emailUserName,
      emailPassword: val.emailPassword,
      smtpHostName: val.smtpHostName,
      smtpPort: Number(val.smtpPort),
      transportStrategy: val.transportStrategy,
      rxHostName: val.rxHostName,
      rxPort: Number(val.rxPort),
      rxProtocol: val.rxProtocol,
      inboxFolderName: val.inboxFolderName,
      accessToken: userEditState?.emailSettings?.accessToken || null,
      refreshToken: userEditState?.emailSettings?.refreshToken || null,
      expireTimeInSeconds: userEditState?.emailSettings?.expireTimeInSeconds || null
    }

    setTestingReceiver(true)
    testReceiveSettings(emailSettings).then(() => {
      showSuccess(t('mailTemplate.users.modal.receiverTestSuccess'))
    }).catch(() => {
      showError(t('mailTemplate.users.modal.receiverTestFailed'))
    }).finally(() => {
      setTestingReceiver(false)
    })
  }, [showSuccess, showError, t, connectionType, userEditState?.emailSettings?.accessToken, userEditState?.emailSettings?.refreshToken, userEditState?.emailSettings?.expireTimeInSeconds])

  const onSubmit = useCallback((val) => {
    const currentEmailSettings = {
      emailUserName: val.emailUserName,
      emailPassword: val.emailPassword,
      smtpHostName: val.smtpHostName,
      smtpPort: Number(val.smtpPort),
      transportStrategy: val.transportStrategy,
      rxHostName: val.rxHostName,
      rxPort: Number(val.rxPort),
      rxProtocol: val.rxProtocol,
      inboxFolderName: val.inboxFolderName
    }

    const normalizedInitialSettings = initialEmailSettingsRef.current
      ? {
          ...initialEmailSettingsRef.current,
          smtpPort: Number(initialEmailSettingsRef.current.smtpPort),
          rxPort: Number(initialEmailSettingsRef.current.rxPort)
        }
      : null

    const hasEmailSettingsChanged = normalizedInitialSettings && !isEqual(currentEmailSettings, normalizedInitialSettings)

    const payload = {
      senderCompany: {
        name: val.senderCompany?.name || userEditState?.senderCompany?.name || '',
        industry: val.senderCompany?.industry || userEditState?.senderCompany?.industry || '',
        country: val.senderCompany?.country || userEditState?.senderCompany?.country || '',
        contact_person: val.senderCompany?.contact_person || userEditState?.senderCompany?.contact_person || '',
        contact_title: val.senderCompany?.contact_title || userEditState?.senderCompany?.contact_title || '',
        email: val.senderCompany?.email || userEditState?.senderCompany?.email || '',
        key_products: val.senderCompany?.key_products?.map(item => typeof item === 'string' ? item : item.value) || userEditState?.senderCompany?.key_products || [],
        website: val.senderCompany?.website || userEditState?.senderCompany?.website || '',
        phone: val.senderCompany?.phone || userEditState?.senderCompany?.phone || '',
        production_capacity: val.senderCompany?.production_capacity || userEditState?.senderCompany?.production_capacity || '',
        facilities: val.senderCompany?.facilities?.map(item => typeof item === 'string' ? item : item.value) || userEditState?.senderCompany?.facilities || [],
        certifications: val.senderCompany?.certifications?.map(item => typeof item === 'string' ? item : item.value) || userEditState?.senderCompany?.certifications || [],
        market_experience: val.senderCompany?.market_experience?.map(item => typeof item === 'string' ? item : item.value) || userEditState?.senderCompany?.market_experience || [],
        company_values: val.senderCompany?.company_values?.map(item => typeof item === 'string' ? item : item.value) || userEditState?.senderCompany?.company_values || []
      },
      emailPurpose: {
        target_market: val.emailPurpose?.target_market || userEditState?.emailPurpose?.target_market || '',
        trade_type: val.emailPurpose?.trade_type || userEditState?.emailPurpose?.trade_type || TRADE_TYPES.EXPORT,
        product_category: val.emailPurpose?.product_category || userEditState?.emailPurpose?.product_category || '',
        language: val.emailPurpose?.language || userEditState?.emailPurpose?.language || LANGUAGES.ENGLISH,
        specific_products: val.emailPurpose?.specific_products?.map(item => typeof item === 'string' ? item : item.value) || userEditState?.emailPurpose?.specific_products || [],
        call_to_action: val.emailPurpose?.call_to_action || userEditState?.emailPurpose?.call_to_action || ''
      }
    }

    if (hasEmailSettingsChanged) {
      payload.emailSettings = currentEmailSettings
    }

    setLoading(true)
    updateUser(userEditState?.id, payload).then(() => {
      getUserDetailsAndProcess(true, userGSD, setUserData, true)
      showSuccess(t('mailTemplate.users.modal.toastSuccess'))
    }).catch((err) => {
      err && showError(t('mailTemplate.users.modal.toastErr'))
    }).finally(() => {
      setLoading(false)
      handleClose()
    })
  }, [
    userEditState?.senderCompany?.name,
    userEditState?.senderCompany?.industry,
    userEditState?.senderCompany?.country,
    userEditState?.senderCompany?.contact_person,
    userEditState?.senderCompany?.contact_title,
    userEditState?.senderCompany?.email,
    userEditState?.senderCompany?.key_products,
    userEditState?.senderCompany?.website,
    userEditState?.senderCompany?.phone,
    userEditState?.senderCompany?.production_capacity,
    userEditState?.senderCompany?.facilities,
    userEditState?.senderCompany?.certifications,
    userEditState?.senderCompany?.market_experience,
    userEditState?.senderCompany?.company_values,
    userEditState?.emailPurpose?.target_market,
    userEditState?.emailPurpose?.trade_type,
    userEditState?.emailPurpose?.product_category,
    userEditState?.emailPurpose?.language,
    userEditState?.emailPurpose?.specific_products,
    userEditState?.emailPurpose?.call_to_action,
    userEditState?.id,
    userGSD,
    setUserData,
    showError,
    showSuccess,
    t,
    handleClose])

  useEffect(() => {
    reset(defaultValues)

    // Update connection type based on account type
    setConnectionType(
      userEditState?.emailSettings?.accountType === 'GMAIL_OAUTH2' ? 'gmail' : 'manual'
    )

    initialEmailSettingsRef.current = {
      emailUserName: userEditState?.emailSettings?.emailUserName || '',
      emailPassword: userEditState?.emailSettings?.emailPassword || '',
      smtpHostName: userEditState?.emailSettings?.smtpHostName || '',
      smtpPort: userEditState?.emailSettings?.smtpPort || '',
      transportStrategy: userEditState?.emailSettings?.transportStrategy || '',
      rxHostName: userEditState?.emailSettings?.rxHostName || '',
      rxPort: userEditState?.emailSettings?.rxPort || '',
      rxProtocol: userEditState?.emailSettings?.rxProtocol || '',
      inboxFolderName: userEditState?.emailSettings?.inboxFolderName || ''
    }
  }, [reset, defaultValues, userEditState?.emailSettings])

  return (
    <Dialog
      fullWidth
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      scroll="paper"
      sx={{
        '& .MuiDialog-paper': {
          overflow: 'visible',
          maxHeight: '90vh',
          borderRadius: 3
        }
      }}
    >
      <DialogCloseButton onClick={close} disableRipple>
        <CloseIcon/>
      </DialogCloseButton>
      <DialogTitle
        variant="h4"
        className="flex gap-2 flex-col text-center sm:pbs-16 sm:pbe-6 sm:pli-16"
      >
        {t('mailTemplate.users.modal.subTitle')}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Tabs
          value={activeTab}
          onChange={handleChange}
          aria-label="email settings tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={t('mailTemplate.wizard.ai.businessCompanyInfo')} value="1"/>
          <Tab label={t('mailTemplate.wizard.ai.emailPurpose')} value="2"/>
          <Tab label={t('signIn.mailInfo')} value="3"/>
        </Tabs>
        <DialogContent
          className="pbs-0 sm:pli-16"
          sx={{
            overflow: 'auto',
            maxHeight: '60vh',
            pt: 2,
            pb: 6,
            '&::-webkit-scrollbar': {
              width: '8px'
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.3)'
              }
            },
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent'
          }}
        >

          {activeTab === '1' &&
            <Box className="p-3" style={{ minHeight: '25rem' }}>
              <BusinessCompanyInfoSection
                control={control}
                fieldPrefix="senderCompany"
                validationRules={companyValidationRules}
              />
            </Box>}

          {activeTab === '2' &&
            <Box className="p-3" style={{ minHeight: '25rem' }}>
              <EmailPurposeTargetingSection
                control={control}
                fieldPrefix="emailPurpose"
              />
            </Box>}

          {activeTab === '3' &&
            <Box className="p-3" style={{ minHeight: '25rem' }}>
              <Box sx={{ mb: 4 }}>
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                  <RadioGroup
                    value={connectionType}
                    onChange={handleConnectionTypeChange}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 3,
                      justifyContent: 'space-between'
                    }}
                  >
                    <Card
                      variant="outlined"
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 3,
                        flex: 1,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.02)'
                        }
                      }}
                      onClick={() => setConnectionType('manual')}
                    >
                      <CardContent sx={{ py: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <Radio
                            checked={connectionType === 'manual'}
                            onChange={handleConnectionTypeChange}
                            value="manual"
                          />
                          <SettingsIcon sx={{ color: 'primary.main' }}/>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.9rem' }}>
                              {t('mailTemplate.users.modal.manualConfiguration')}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.primary', opacity: 0.7, mt: 0.2, display: 'block' }}>
                              {t('mailTemplate.users.modal.manualDescription')}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>

                    <Card
                      variant="outlined"
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 3,
                        flex: 1,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.02)'
                        }
                      }}
                      onClick={() => setConnectionType('gmail')}
                    >
                      <CardContent sx={{ py: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <Radio
                            checked={connectionType === 'gmail'}
                            onChange={handleConnectionTypeChange}
                            value="gmail"
                          />
                          <EmailIcon sx={{ color: 'primary.main' }}/>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.9rem' }}>
                              {t('mailTemplate.users.modal.connectWithGmail')}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.primary', opacity: 0.7, mt: 0.2, display: 'block' }}>
                              {t('mailTemplate.users.modal.gmailDescription')}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </RadioGroup>
                </FormControl>
              </Box>

              {connectionType === 'gmail' && (
                <Box sx={{ mt: 3 }}>
                  <Card variant="outlined" sx={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e0e0e0',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                  }}>
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ mb: 8, textAlign: 'center' }}>
                        <EmailIcon sx={{
                          fontSize: 48,
                          color: 'primary.main',
                          mb: 2,
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                        }}/>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          {t('mailTemplate.users.modal.gmailIntegration')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
                          {t('mailTemplate.users.modal.gmailIntegrationDescription')}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <GmailConnectButton
                          langCode={languageGSD}
                          email={userEditState?.emailSettings?.emailUserName}
                          connected={userEditState?.emailSettings?.accountType === 'GMAIL_OAUTH2'}
                          onDisconnect={() => {
                            // TODO: Implement disconnect functionality
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              )}

              {connectionType === 'manual' && (
                <Box sx={{ mt: 3 }}>
                  <Card variant="outlined" sx={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e0e0e0',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                  }}>
                    <CardContent sx={{ p: 4 }}>
                      <Box sx={{ mb: 10, textAlign: 'center' }}>
                        <SettingsIcon sx={{
                          fontSize: 48,
                          color: 'primary.main',
                          mb: 2,
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                        }}/>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          {t('mailTemplate.users.modal.manualConfiguration')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
                          {t('mailTemplate.users.modal.manualConfigurationDescription')}
                        </Typography>
                      </Box>

                      <Grid container spacing={6}>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ mb: 4 }}>
                            <Typography variant="subtitle1" sx={{ mb: 2.5, color: 'text.primary' }}>
                              {t('mailTemplate.users.modal.emailInformation')}
                            </Typography>
                            <Grid container spacing={4}>
                              <Grid item xs={12}>
                                <Controller
                                  name="emailUserName"
                                  control={control}
                                  // rules={{
                                  //   validate: {
                                  //     required: (value) => emailValidation(value)
                                  //   }
                                  // }}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      variant="outlined"
                                      size="small"
                                      label={t('mailTemplate.users.modal.emailUserName')}
                                      placeholder={t(
                                        'mailTemplate.users.modal.emailUserNameP'
                                      )}
                                      {...(errors.emailUserName && {
                                        error: true,
                                        helperText: t(
                                          'mailTemplate.users.modal.errors.emailUserName'
                                        )
                                      })}
                                    />
                                  )}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <Controller
                                  name="emailPassword"
                                  control={control}
                                  // rules={{ required: true }}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      variant="outlined"
                                      size="small"
                                      label={t('mailTemplate.users.modal.emailPassword')}
                                      placeholder={t(
                                        'mailTemplate.users.modal.emailPasswordP'
                                      )}
                                      type={isPasswordShown ? 'text' : 'password'}
                                      InputProps={{
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <IconButton
                                              edge="end"
                                              onClick={handleClickShowPassword}
                                              onMouseDown={(e) => e.preventDefault()}
                                              aria-label="toggle password visibility"
                                            >
                                              {isPasswordShown
                                                ? (
                                                  <VisibilityOffIcon/>
                                                  )
                                                : (
                                                  <VisibilityIcon/>
                                                  )}
                                            </IconButton>
                                          </InputAdornment>
                                        )
                                      }}
                                      {...(errors.emailPassword && {
                                        error: true,
                                        helperText: t(
                                          'mailTemplate.users.modal.errors.emailPassword'
                                        )
                                      })}
                                    />
                                  )}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <Controller
                                  name="inboxFolderName"
                                  control={control}
                                  // rules={{ required: true }}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      variant="outlined"
                                      size="small"
                                      label={t(
                                        'mailTemplate.users.modal.inboxFolderName')}
                                      placeholder={t(
                                        'mailTemplate.users.modal.inboxFolderNameP'
                                      )}
                                      {...(errors.inboxFolderName && {
                                        error: true,
                                        helperText: t(
                                          'mailTemplate.users.modal.errors.inboxFolderName'
                                        )
                                      })}
                                    />
                                  )}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <Box sx={{ mb: 4 }}>
                            <Typography variant="subtitle1" sx={{ mb: 2.5, color: 'text.primary' }}>
                              {t('mailTemplate.users.modal.senderInformation')}
                            </Typography>
                            <Grid container spacing={4}>
                              <Grid item xs={12}>
                                <Controller
                                  name="smtpHostName"
                                  control={control}
                                  // rules={{ required: true }}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      variant="outlined"
                                      size="small"
                                      label={t('mailTemplate.users.modal.smtpHostName')}
                                      placeholder={t(
                                        'mailTemplate.users.modal.smtpHostNameP'
                                      )}
                                      {...(errors.smtpHostName && {
                                        error: true,
                                        helperText: t(
                                          'mailTemplate.users.modal.errors.smtpHostName'
                                        )
                                      })}
                                    />
                                  )}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <Controller
                                  name="smtpPort"
                                  control={control}
                                  // rules={{ required: true }}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      variant="outlined"
                                      size="small"
                                      label={t('mailTemplate.users.modal.smtpPort')}
                                      placeholder={t(
                                        'mailTemplate.users.modal.smtpPortP')}
                                      type="number"
                                      {...(errors.smtpPort && {
                                        error: true,
                                        helperText: t(
                                          'mailTemplate.users.modal.errors.smtpPort'
                                        )
                                      })}
                                    />
                                  )}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <Controller
                                  name="transportStrategy"
                                  control={control}
                                  // rules={{ required: true }}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      select
                                      variant="outlined"
                                      size="small"
                                      label={t(
                                        'mailTemplate.users.modal.transportStrategy')}
                                      defaultValue=""
                                      {...(errors.transportStrategy && {
                                        error: true,
                                        helperText: t(
                                          'mailTemplate.users.modal.errors.transportStrategy'
                                        )
                                      })}
                                    >
                                      {transportStrategyOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                          {option.label}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                  )}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <Box>
                            <Typography variant="subtitle1" sx={{ mb: 2.5, color: 'text.primary' }}>
                              {t('mailTemplate.users.modal.receiverInformation')}
                            </Typography>
                            <Grid container spacing={4}>
                              <Grid item xs={12}>
                                <Controller
                                  name="rxHostName"
                                  control={control}
                                  // rules={{ required: true }}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      variant="outlined"
                                      size="small"
                                      label={t('mailTemplate.users.modal.rxHostName')}
                                      placeholder={t(
                                        'mailTemplate.users.modal.rxHostNameP')}
                                      {...(errors.rxHostName && {
                                        error: true,
                                        helperText: t(
                                          'mailTemplate.users.modal.errors.rxHostName'
                                        )
                                      })}
                                    />
                                  )}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <Controller
                                  name="rxPort"
                                  control={control}
                                  // rules={{ required: true }}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      variant="outlined"
                                      size="small"
                                      label={t('mailTemplate.users.modal.rxPort')}
                                      placeholder={t('mailTemplate.users.modal.rxPortP')}
                                      type="number"
                                      {...(errors.rxPort && {
                                        error: true,
                                        helperText: t(
                                          'mailTemplate.users.modal.errors.rxPort'
                                        )
                                      })}
                                    />
                                  )}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <Controller
                                  name="rxProtocol"
                                  control={control}
                                  // rules={{ required: true }}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      select
                                      variant="outlined"
                                      size="small"
                                      label={t('mailTemplate.users.modal.rxProtocol')}
                                      defaultValue=""
                                      {...(errors.rxProtocol && {
                                        error: true,
                                        helperText: t(
                                          'mailTemplate.users.modal.errors.rxProtocol'
                                        )
                                      })}
                                    >
                                      {emailReceiveProtocolOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                          {option.label}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                  )}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Box>
              )}
            </Box>}
        </DialogContent>
        <DialogActions sx={{ px: 12, py: 3, gap: 1, justifyContent: 'space-between', mb: 4, mt: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {activeTab === '3' && connectionType === 'manual' && (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={isTestSenderDisabled}
                  startIcon={testingSender ? <CircularProgress size={16}/> : null}
                  onClick={handleSubmit(testSenderSettings)}
                  sx={{ borderRadius: 2 }}
                >
                  {testingSender ? t('mailTemplate.users.modal.testingSender') : t('mailTemplate.users.modal.testSender')}
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={isTestReceiverDisabled}
                  startIcon={testingReceiver ? <CircularProgress size={16}/> : null}
                  onClick={handleSubmit(testReceiverSettings)}
                  sx={{ borderRadius: 2 }}
                >
                  {testingReceiver ? t('mailTemplate.users.modal.testingReceiver') : t('mailTemplate.users.modal.testReceiver')}
                </Button>
              </>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={handleClose}
              variant="outlined"
              color="inherit"
              type="reset"
              sx={{ borderRadius: 2 }}
            >
              {t('mailTemplate.users.modal.cancel')}
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={loading}
              startIcon={loading
                ? <CircularProgress sx={{ color: '#fff' }}
                                    size={20}/>
                : null}
              autoFocus
              sx={{ borderRadius: 2 }}
            >
              {t('mailTemplate.wizard.rte.saveBtn')}
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default EditUserEmailSettingsModal
